import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Settings, X } from 'lucide-react';
import { useDAWStore } from '../../store/daw-store';
import type { MacroParameter } from '../../store/daw-store';

export const MacroControls = () => {
  const { macros, addMacro, removeMacro, updateMacro, updateMacroName } = useDAWStore();
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedMacroId, setSelectedMacroId] = useState<string | null>(null);

  const handleAddMacro = () => {
    addMacro({
      name: `Macro ${macros.length + 1}`,
      value: 0.5,
      parameters: [],
    });
  };

  const handleOpenAssignDialog = (macroId: string) => {
    setSelectedMacroId(macroId);
    setShowAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setShowAssignDialog(false);
    setSelectedMacroId(null);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-white">Macro Controls</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Control multiple parameters with a single knob
          </p>
        </div>
        <button
          onClick={handleAddMacro}
          disabled={macros.length >= 8}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Macro
        </button>
      </div>

      {/* Macro Knobs Grid */}
      {macros.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <p className="mb-4">No macros created yet</p>
          <p className="text-sm">Click "Add Macro" to create your first macro control</p>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-6">
          {macros.map((macro) => (
            <MacroKnob
              key={macro.id}
              macro={macro}
              onValueChange={(value) => updateMacro(macro.id, value)}
              onNameChange={(name) => updateMacroName(macro.id, name)}
              onDelete={() => removeMacro(macro.id)}
              onOpenAssign={() => handleOpenAssignDialog(macro.id)}
            />
          ))}
        </div>
      )}

      {/* Parameter Assignment Dialog */}
      {showAssignDialog && selectedMacroId && (
        <ParameterAssignDialog
          macroId={selectedMacroId}
          onClose={handleCloseAssignDialog}
        />
      )}
    </div>
  );
};

interface MacroKnobProps {
  macro: { id: string; name: string; value: number; parameters: MacroParameter[] };
  onValueChange: (value: number) => void;
  onNameChange: (name: string) => void;
  onDelete: () => void;
  onOpenAssign: () => void;
}

const MacroKnob = ({ macro, onValueChange, onNameChange, onDelete, onOpenAssign }: MacroKnobProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(macro.name);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startValue, setStartValue] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartY(e.clientY);
    setStartValue(macro.value);
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const deltaY = startY - e.clientY;
    const sensitivity = 0.005;
    const newValue = Math.max(0, Math.min(1, startValue + deltaY * sensitivity));
    
    onValueChange(newValue);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleNameSubmit = () => {
    if (editName.trim()) {
      onNameChange(editName.trim());
    }
    setIsEditing(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setEditName(macro.name);
      setIsEditing(false);
    }
  };

  // Add/remove mouse move and up listeners
  if (isDragging) {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  } else {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }

  const rotation = (macro.value - 0.5) * 270; // -135° to +135°
  const percentage = Math.round(macro.value * 100);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 flex flex-col items-center"
    >
      {/* Knob */}
      <div className="relative mb-3">
        <div
          className={`w-20 h-20 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 border-2 border-zinc-600 cursor-pointer shadow-lg ${
            isDragging ? 'ring-2 ring-indigo-500' : ''
          }`}
          onMouseDown={handleMouseDown}
        >
          {/* Knob indicator */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            <div className="w-1 h-8 bg-indigo-500 rounded-full" style={{ marginTop: '-16px' }} />
          </div>
          
          {/* Center dot */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 bg-zinc-900 rounded-full" />
          </div>
        </div>
        
        {/* Value display */}
        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-zinc-400 font-mono">
          {percentage}%
        </div>
      </div>

      {/* Name */}
      <div className="mt-6 w-full">
        {isEditing ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleNameSubmit}
            onKeyDown={handleNameKeyDown}
            className="w-full px-2 py-1 bg-zinc-900 border border-zinc-600 rounded text-sm text-white text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
            autoFocus
          />
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="w-full px-2 py-1 text-sm text-white hover:bg-zinc-700 rounded transition-colors text-center truncate"
          >
            {macro.name}
          </button>
        )}
      </div>

      {/* Assigned Parameters Count */}
      <div className="text-xs text-zinc-500 mt-2">
        {macro.parameters.length} parameter{macro.parameters.length !== 1 ? 's' : ''}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3 w-full">
        <button
          onClick={onOpenAssign}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded transition-colors"
          title="Assign Parameters"
        >
          <Settings className="w-3 h-3" />
          Assign
        </button>
        <button
          onClick={onDelete}
          className="px-2 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded transition-colors"
          title="Delete Macro"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </motion.div>
  );
};

interface ParameterAssignDialogProps {
  macroId: string;
  onClose: () => void;
}

const ParameterAssignDialog = ({ macroId, onClose }: ParameterAssignDialogProps) => {
  const { 
    macros, 
    mixerTracks, 
    addMacroParameter, 
    removeMacroParameter, 
    updateMacroParameter 
  } = useDAWStore();
  
  const macro = macros.find((m) => m.id === macroId);
  const [selectedParameter, setSelectedParameter] = useState<string>('');
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(1);

  if (!macro) return null;

  // Available parameters
  const availableParameters = [
    { id: 'global.masterVolume', label: 'Master Volume', min: -60, max: 6 },
    { id: 'global.bpm', label: 'BPM', min: 60, max: 200 },
    ...mixerTracks.flatMap((track) => [
      { id: `${track.id}.volume`, label: `${track.name} - Volume`, min: -60, max: 6 },
      { id: `${track.id}.pan`, label: `${track.name} - Pan`, min: -1, max: 1 },
      { id: `${track.id}.eqLow`, label: `${track.name} - EQ Low`, min: -12, max: 12 },
      { id: `${track.id}.eqMid`, label: `${track.name} - EQ Mid`, min: -12, max: 12 },
      { id: `${track.id}.eqHigh`, label: `${track.name} - EQ High`, min: -12, max: 12 },
      { id: `${track.id}.sendA`, label: `${track.name} - Send A`, min: 0, max: 1 },
      { id: `${track.id}.sendB`, label: `${track.name} - Send B`, min: 0, max: 1 },
      { id: `${track.id}.stereoWidth`, label: `${track.name} - Stereo Width`, min: 0, max: 2 },
    ]),
  ];

  const handleAddParameter = () => {
    if (!selectedParameter || macro.parameters.length >= 8) return;
    
    const param = availableParameters.find((p) => p.id === selectedParameter);
    if (!param) return;

    // Check if parameter already assigned
    if (macro.parameters.some((p) => p.parameterId === selectedParameter)) {
      alert('This parameter is already assigned to this macro');
      return;
    }

    addMacroParameter(macroId, {
      parameterId: selectedParameter,
      parameterLabel: param.label,
      min: minValue,
      max: maxValue,
    });

    // Reset form
    setSelectedParameter('');
    setMinValue(0);
    setMaxValue(1);
  };

  const handleParameterSelect = (parameterId: string) => {
    setSelectedParameter(parameterId);
    const param = availableParameters.find((p) => p.id === parameterId);
    if (param) {
      setMinValue(param.min);
      setMaxValue(param.max);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700">
          <div>
            <h3 className="text-lg font-semibold text-white">Assign Parameters</h3>
            <p className="text-sm text-zinc-400 mt-1">
              {macro.name} - {macro.parameters.length}/8 parameters assigned
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Parameter Form */}
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-white mb-3">Add Parameter</h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Parameter</label>
                <select
                  value={selectedParameter}
                  onChange={(e) => handleParameterSelect(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={macro.parameters.length >= 8}
                >
                  <option value="">Select a parameter...</option>
                  {availableParameters.map((param) => (
                    <option key={param.id} value={param.id}>
                      {param.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Min Value</label>
                  <input
                    type="number"
                    value={minValue}
                    onChange={(e) => setMinValue(parseFloat(e.target.value))}
                    step="0.1"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Max Value</label>
                  <input
                    type="number"
                    value={maxValue}
                    onChange={(e) => setMaxValue(parseFloat(e.target.value))}
                    step="0.1"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <button
                onClick={handleAddParameter}
                disabled={!selectedParameter || macro.parameters.length >= 8}
                className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                Add Parameter
              </button>
            </div>
          </div>

          {/* Assigned Parameters List */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Assigned Parameters</h4>
            
            {macro.parameters.length === 0 ? (
              <div className="text-center py-8 text-zinc-400 text-sm">
                No parameters assigned yet
              </div>
            ) : (
              <div className="space-y-2">
                {macro.parameters.map((param) => (
                  <div
                    key={param.parameterId}
                    className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="text-sm text-white font-medium">{param.parameterLabel}</div>
                      <div className="text-xs text-zinc-400 mt-1">
                        Range: {param.min.toFixed(2)} to {param.max.toFixed(2)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeMacroParameter(macroId, param.parameterId)}
                      className="p-2 hover:bg-red-600/20 text-red-400 rounded transition-colors"
                      title="Remove Parameter"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded transition-colors"
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
